import styled from "styled-components";
import UserAvatar from "../../common/components/UserAvatar";
import UpdateUserButton from "./UpdateUserButton";
import { UserType } from "global";
import Loader from "../../common/components/Loader";
import DeleteUserButton from "./DeleteUserButton";
import PromoteToPartnerButton from "./PromoteToPartnerButton";
import { Link } from "react-router-dom";
import BoxWithShadow from "../../common/components/BoxWithShadow";

interface ProfileSectionProps {
  name: string;
  id: string;
  src?: string;
  role?: string;
  alt?: string;
  children?: React.ReactNode;
  type: UserType;
  isDeletedUser?: boolean;
  isLoading?: boolean;
}

export default function ProfileSection({
  src,
  alt,
  type,
  name,
  role,
  id,
  children,
  isDeletedUser = false,
  isLoading = false,
}: ProfileSectionProps) {
  const isReceiverProfile = type === "receivers";
  const isCaregiverProfile = type === "caregivers";

  if (isLoading) {
    return (
      <Wrapper>
        <Loader />
      </Wrapper>
    );
  }

  return (
    <BoxWithShadow className="w-1/4">
      <AvatarWrapper>
        <UserAvatar src={src} alt={alt} size={100} />
        <Name>{name}</Name>
        <ID>{id}</ID>
      </AvatarWrapper>

      <InfoWrapper>{children}</InfoWrapper>
      <ButtonsWrapper>
        {isCaregiverProfile && (
          <PromoteToPartnerButton
            userId={id}
            userType={type}
            isPartner={!!role && ["ADMIN", "SUB_ADMIN"].includes(role)}
          />
        )}
        <div className="flex w-full gap-1">
          {isReceiverProfile && (
            <Link
              to={`/users/records/receiver/${id}`}
              className="flex w-full items-center justify-center rounded-md border border-zinc-500 text-base"
            >
              <p className="flex items-center gap-2">돌봄기록 현황</p>
            </Link>
          )}
          <UpdateUserButton
            userId={id}
            type={type}
            isDisabled={isDeletedUser}
          />
          {!isReceiverProfile && (
            <DeleteUserButton
              userId={id}
              type={type}
              isDisabled={isDeletedUser}
            />
          )}
        </div>
      </ButtonsWrapper>
    </BoxWithShadow>
  );
}

const Wrapper = styled.section(({ theme }) => ({
  border: `1px solid ${theme.color.darkGray}`,
  backgroundColor: theme.color.white,
  height: "100%",
  width: "25%",
  padding: theme.spacing.middle,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  fontSize: 14,
  borderRadius: 10,
}));

const AvatarWrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  alignItems: "center",
}));

const ID = styled.p(({ theme }) => ({
  margin: 0,
  fontSize: 12,
  color: theme.color.darkGray,
}));

const Name = styled.p(() => ({
  margin: 0,
  fontSize: "1rem",
  fontWeight: "bold",
  marginTop: 10,
}));

const InfoWrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  marginTop: 20,
}));

const ButtonsWrapper = styled.div(() => ({
  marginTop: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 5,
}));
